import { useI18n } from '../i18n.tsx';
import type { VerificationState, VerificationStatus } from '../types.ts';
import { STATUS_ORDER } from '../constants.ts';

function relativeTime(iso: string, lang: 'ro' | 'en'): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60_000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(-hours, 'hour');
  return rtf.format(-Math.round(hours / 24), 'day');
}

export function VerifyBar({
  state,
  counts,
  activeStatus,
  onToggleStatus,
  onVerify,
}: {
  state: VerificationState | undefined;
  counts: Record<string, number>;
  activeStatus: string;
  onToggleStatus: (status: string) => void;
  onVerify: () => void;
}) {
  const { t, lang } = useI18n();
  const running = state?.progress.running ?? false;
  const lastRun = state?.lastRun ?? null;

  return (
    <div className="verify-bar">
      <div className="verify-meta">
        <span className="label">{t.lastChecked}</span>
        <span className="value">
          {lastRun ? relativeTime(lastRun.runAt, lang) : t.never}
          {lastRun && (
            <span style={{ color: 'var(--muted)' }}>
              {' · '}
              {lastRun.sources.filter((s) => s.ok).length}/{lastRun.sources.length}{' '}
              {t.sourcesChecked}
            </span>
          )}
        </span>
      </div>

      <div className="status-tally">
        {STATUS_ORDER.filter((s) => (counts[s] ?? 0) > 0).map((status) => (
          <button
            key={status}
            type="button"
            className={`tally badge s-${status} ${activeStatus === status ? 'is-active' : ''}`}
            aria-pressed={activeStatus === status}
            title={t.statusHelp[status as VerificationStatus]}
            onClick={() => onToggleStatus(status)}
          >
            <span className="dot" aria-hidden="true" />
            <span className="count">{counts[status]}</span>
            {t.statusLabels[status as VerificationStatus]}
          </button>
        ))}
      </div>

      <button type="button" className="primary-button" onClick={onVerify} disabled={running}>
        {running && <span className="spinner" aria-hidden="true" />}
        {running ? t.verifying : t.verifyNow}
      </button>

      {running && state?.progress.total ? (
        <p className="progress-note">
          {state.progress.done}/{state.progress.total} · {state.progress.stage}
        </p>
      ) : null}
    </div>
  );
}
